import { useMemo } from "react";
import { evalPredictions } from "../lib/predictions";

// Statistikk for journalen: lønner det seg det du gjør? Forventning per handel, avkastningskurve,
// resultat per årsak/signal og per type, og hvor godt kursprediksjonene har truffet.

const C = { bg: "#060f1c", bg2: "#0a1628", border: "#1e293b", dim: "#475569", text: "#94a3b8", bright: "#e2e8f0",
  green: "#10b981", yellow: "#f59e0b", red: "#f43f5e" };
const card = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 14 };
const head = { fontSize: 10, color: C.dim, letterSpacing: 1, fontWeight: 700, marginBottom: 10 };
const mean = a => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
const sum = a => a.reduce((x, y) => x + y, 0);
const fmtP = (v, d = 1) => (v == null ? "–" : `${v >= 0 ? "+" : ""}${v.toFixed(d)}%`);
const fmtM = v => (v == null ? "–" : `${v >= 0 ? "+" : "−"}$${Math.abs(v).toLocaleString("nb-NO", { maximumFractionDigits: 0 })}`);
const col = v => (v == null ? C.dim : v > 0 ? C.green : v < 0 ? C.red : C.text);

function groupStats(trades) {
  const w = trades.filter(t => t.pct > 0);
  return { n: trades.length, win: trades.length ? w.length / trades.length * 100 : null, avg: mean(trades.map(t => t.pct)), pnl: sum(trades.map(t => t.pnl)) };
}

function Equity({ points }) {
  if (points.length < 2) return <div style={{ fontSize: 11, color: C.dim }}>Kurven vises når du har minst to lukkede handler.</div>;
  const W = 600, H = 150, P = 8;
  const ys = points.map(p => p.v), lo = Math.min(0, ...ys), hi = Math.max(0, ...ys), span = hi - lo || 1;
  const x = i => P + (i / (points.length - 1)) * (W - 2 * P), y = v => H - P - ((v - lo) / span) * (H - 2 * P);
  const last = ys[ys.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 160, display: "block" }} preserveAspectRatio="none">
      <line x1={P} x2={W - P} y1={y(0)} y2={y(0)} stroke="#334155" strokeDasharray="4 4" />
      <polyline fill="none" stroke={last >= 0 ? C.green : C.red} strokeWidth="2" points={points.map((p, i) => `${x(i)},${y(p.v)}`).join(" ")} />
      {points.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.v)} r="2.5" fill={p.d >= 0 ? C.green : C.red}><title>{`${p.label}: ${fmtM(p.d)} (sum ${fmtM(p.v)})`}</title></circle>)}
    </svg>
  );
}

export default function JournalStats({ journal, predLog, techData, onOpen }) {
  const closed = useMemo(() => journal
    .filter(t => t.exitDate && t.exitPrice != null && t.price)
    .map(t => {
      const mult = t.tradeType === "opsjon" ? 100 : 1;
      return { ...t, pct: (t.exitPrice - t.price) / t.price * 100, pnl: (t.exitPrice - t.price) * (parseFloat(t.qty) || 0) * mult };
    })
    .sort((a, b) => a.exitDate.localeCompare(b.exitDate)), [journal]);

  const n = closed.length;
  const wins = closed.filter(t => t.pct > 0), losses = closed.filter(t => t.pct <= 0);
  const avgW = mean(wins.map(t => t.pct)), avgL = mean(losses.map(t => t.pct));
  const expectancy = n ? (wins.length / n) * (avgW || 0) + (losses.length / n) * (avgL || 0) : null;
  const grossW = sum(wins.map(t => t.pnl)), grossL = -sum(losses.map(t => t.pnl));
  const pf = grossL > 0 ? grossW / grossL : null;
  let run = 0, peak = 0, maxDD = 0;
  const equity = closed.map(t => {
    run += t.pnl; peak = Math.max(peak, run); maxDD = Math.min(maxDD, run - peak);
    return { v: run, d: t.pnl, label: `${t.exitDate} ${t.ticker}` };
  });

  const by = keyFn => {
    const g = {};
    closed.forEach(t => { const k = keyFn(t); (g[k] ||= []).push(t); });
    return Object.entries(g).map(([k, ts]) => ({ k, ...groupStats(ts) })).sort((a, b) => b.pnl - a.pnl);
  };
  const byReason = by(t => t.reason || "Annet");
  const byType = by(t => (t.tradeType === "opsjon" ? `Opsjon (${t.optionType === "put" ? "put" : "call"})` : "Aksje"));

  const preds = useMemo(() => evalPredictions(predLog, techData), [predLog, techData]);
  const pErr = mean(preds.map(p => p.err)), pNaive = mean(preds.map(p => p.naive));
  const dirs = preds.filter(p => p.dirOk != null), pDir = dirs.length ? dirs.filter(p => p.dirOk).length / dirs.length * 100 : null;

  const advice = n === 0 ? "Logg handlene dine i Journal-fanen, så ser du her om det du gjør faktisk lønner seg."
    : n < 20 ? `Du har ${n} lukkede handler. Under 20–30 er tallene mest støy — hold posisjonene små og fortsett å logge.`
    : expectancy > 0 ? "Positiv forventning per handel. Fortsett med det som virker (se tabellen under), og øk størrelsen forsiktig."
    : "Negativ forventning per handel. Se hvilke årsaker som taper, og slutt med dem før du øker størrelsen.";

  const box = (label, val, color, sub) => (
    <div style={{ background: C.bg2, borderRadius: 6, padding: "10px 12px", flex: "1 1 130px" }}>
      <div style={{ fontSize: 9, color: C.dim, letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color }}>{val}</div>
      {sub && <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{sub}</div>}
    </div>
  );
  const table = (rows, title) => (
    <div style={card}>
      <div style={head}>{title}</div>
      {rows.length ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
            <thead><tr>{["", "HANDLER", "TREFFRATE", "SNITT", "RESULTAT"].map(h => <th key={h} style={{ textAlign: h ? "right" : "left", fontSize: 9, color: C.dim, letterSpacing: 1, padding: "4px 6px" }}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.k} style={{ borderTop: `1px solid ${C.bg2}` }}>
                  <td style={{ padding: "6px", fontSize: 12, color: C.text }}>{r.k}</td>
                  <td style={{ padding: "6px", fontSize: 12, color: r.n < 5 ? C.dim : C.text, textAlign: "right" }}>{r.n}{r.n < 5 ? " ·få" : ""}</td>
                  <td style={{ padding: "6px", fontSize: 12, textAlign: "right", color: r.win >= 50 ? C.green : C.red }}>{r.win?.toFixed(0)}%</td>
                  <td style={{ padding: "6px", fontSize: 12, textAlign: "right", color: col(r.avg) }}>{fmtP(r.avg)}</td>
                  <td style={{ padding: "6px", fontSize: 12, textAlign: "right", fontWeight: 700, color: col(r.pnl) }}>{fmtM(r.pnl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div style={{ fontSize: 11, color: C.dim }}>Ingen lukkede handler ennå.</div>}
    </div>
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={card}>
        <div style={head}>LØNNER DET SEG?</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          {box("FORVENTNING PER HANDEL", fmtP(expectancy, 2), col(expectancy), "snitt du tjener per handel")}
          {box("TREFFRATE", n ? `${(wins.length / n * 100).toFixed(0)}%` : "–", n && wins.length / n >= 0.5 ? C.green : C.yellow, `${wins.length} av ${n}`)}
          {box("SNITT GEVINST / TAP", n ? `${fmtP(avgW)} / ${fmtP(avgL)}` : "–", C.bright, "store vinnere, små tap = bra")}
          {box("PROFITTFAKTOR", pf != null ? pf.toFixed(2) : "–", pf != null ? (pf >= 1.5 ? C.green : pf >= 1 ? C.yellow : C.red) : C.dim, "gevinst ÷ tap (over 1,5 er bra)")}
          {box("TOTALT", fmtM(sum(closed.map(t => t.pnl))), col(sum(closed.map(t => t.pnl))), `største fall ${fmtM(maxDD)}`)}
        </div>
        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6 }}>{advice}</div>
      </div>

      <div style={card}>
        <div style={head}>AVKASTNINGSKURVE (SUM AV LUKKEDE HANDLER)</div>
        <Equity points={equity} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(380px,1fr))", gap: 14 }}>
        <div>{table(byReason, "PER ÅRSAK / SIGNAL")}</div>
        <div>{table(byType, "PER TYPE")}</div>
      </div>

      <div style={card}>
        <div style={head}>KURSPREDIKSJONENE · TREFFER DE?</div>
        {preds.length ? (
          <>
            <div style={{ fontSize: 12, color: C.text, marginBottom: 10, lineHeight: 1.6 }}>
              {preds.length} evaluert: snittavvik <b style={{ color: pErr < pNaive ? C.green : C.red }}>{pErr.toFixed(2)}%</b> mot {pNaive.toFixed(2)}% for «kursen står stille»
              {pDir != null && <> · riktig retning <b style={{ color: pDir > 50 ? C.green : C.red }}>{pDir.toFixed(0)}%</b></>}.
              {" "}{pErr < pNaive ? "Prediksjonen er bedre enn å gjette uendret kurs." : "Foreløpig ikke bedre enn å gjette uendret kurs."}
            </div>
            {preds.slice(0, 12).map(p => (
              <div key={p.id} onClick={() => onOpen(p.ticker)} style={{ display: "grid", gridTemplateColumns: "56px 90px 1fr 70px", gap: 8, fontSize: 11, padding: "3px 0", cursor: "pointer", color: C.text }}>
                <b style={{ color: C.bright }}>{p.ticker}</b>
                <span style={{ color: C.dim }}>{p.target}</span>
                <span>${p.cp} → spådd ${p.pred} · faktisk ${p.actual.toFixed(2)}</span>
                <span style={{ textAlign: "right", color: p.err < p.naive ? C.green : C.red }}>{p.dirOk == null ? "" : p.dirOk ? "✓ retning" : "✗ retning"}</span>
              </div>
            ))}
          </>
        ) : <div style={{ fontSize: 11, color: C.dim }}>{predLog.length} prediksjoner logget. Resultater kommer når måldatoene er passert (åpne aksjene i Aksje-fanen for å logge flere).</div>}
      </div>
    </div>
  );
}
